import { computed, effect, inject } from '@angular/core';
import {
  getState,
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { ApplicationStore } from '../application/application.store';
import { UserStore } from '../user/user.store';
import { withStoreDevtools } from '../with-devtools';
import {
  activeAgentItems,
  activeAgentSubscribedItems,
  adminHomeItems,
  agentItems,
  getOwnerNavigationItems,
  publicOwnerItems,
  publicProItems,
  uiItems,
} from './items.data';
import { NavigationItem, NavigationKeys, NavigationLayout, PublicAudience } from './navigation.interface';

export interface NavigationState {
  key: NavigationKeys | null;
  items: NavigationItem[];
  layout: NavigationLayout | null;
  publicAudience: PublicAudience;
}

const getInitialPublicAudience = (): PublicAudience => {
  if (typeof window !== 'undefined' && window.location.pathname.startsWith('/pro')) {
    return 'pro';
  }
  return 'owner';
};

// initial state
const initialState: NavigationState = {
  key: null,
  items: [],
  layout: null,
  publicAudience: getInitialPublicAudience(),
};

// signal store
export const NavigationStore = signalStore(
  { providedIn: 'root' },
  withStoreDevtools('navigation'),
  withState(initialState),

  withMethods((store) => {
    const userStore = inject(UserStore);

    return {
      updateNavigationKey(key: NavigationKeys) {
        let items: NavigationItem[] = [];
        let layout: NavigationLayout | null = null;
        const user = userStore.current();

        switch (key) {
          case NavigationKeys.AUTH:
            layout = NavigationLayout.BRAND_BANNER;
            break;
          case NavigationKeys.ADMIN_HOME:
            items = adminHomeItems;
            layout = NavigationLayout.SIDEBAR_AUXILIARY;
            break;
          case NavigationKeys.PUBLIC:
            items = store.publicAudience() === 'pro' ? publicProItems : publicOwnerItems;
            layout = NavigationLayout.STACKED;
            break;
          case NavigationKeys.AGENT:
            if (user?.profiles?.agent) {
              const isSubscribed = user?.subscription?.status === 'active';
              items = isSubscribed ? activeAgentSubscribedItems : activeAgentItems;
              layout = NavigationLayout.SIDEBAR_AUXILIARY;
            } else {
              items = agentItems;
              layout = NavigationLayout.STACKED;
            }
            break;
          case NavigationKeys.UI:
            items = uiItems;
            break;
          case NavigationKeys.OWNER:
            items = getOwnerNavigationItems(!!user?.profiles?.owner);
            layout = NavigationLayout.STACKED;
            break;
        }
        patchState(store, { key, items, layout });
      },

      setPublicAudience(audience: PublicAudience) {
        if (store.publicAudience() === audience && store.items().length > 0) {
          return;
        }
        if (store.key() === NavigationKeys.PUBLIC) {
          const items = audience === 'pro' ? publicProItems : publicOwnerItems;
          patchState(store, { publicAudience: audience, items });
        } else {
          patchState(store, { publicAudience: audience });
        }
      },
    };
  }),

  withComputed((store) => ({
    currentKey: computed(() => store.key()),
    currentItems: computed(() => store.items()),
    currentPublicAudience: computed(() => store.publicAudience()),
  })),
  withHooks((store) => {
    const userStore = inject(UserStore);
    const applicationStore = inject(ApplicationStore);

    return {
      onInit() {
        effect(() => {
          const user = userStore.current();
          const key = store.key();
          if (key === NavigationKeys.AGENT) {
            const currentItems = store.items();
            const currentLayout = store.layout();

            const isSubscribed = user?.subscription?.status === 'active';
            const expectedItems = user?.profiles?.agent
              ? isSubscribed
                ? activeAgentSubscribedItems
                : activeAgentItems
              : agentItems;
            const expectedLayout = user?.profiles?.agent
              ? NavigationLayout.SIDEBAR_AUXILIARY
              : NavigationLayout.STACKED;

            if (currentLayout !== expectedLayout || currentItems !== expectedItems) {
              store.updateNavigationKey(key);
            }
          } else if (key === NavigationKeys.OWNER) {
            const currentItems = store.items();
            const expectedItems = getOwnerNavigationItems(!!user?.profiles?.owner);
            const differs =
              !currentItems ||
              currentItems.length !== expectedItems.length ||
              currentItems.some((item, idx) => item.link !== expectedItems[idx]?.link);

            if (differs) {
              store.updateNavigationKey(key);
            }
          }
        });

        effect(() => {
          if (applicationStore.debug()) {
            console.debug('Navigation state:', getState(store));
          }
        });
      },
    };
  }),
);
